import type {
  IPresentationCreatorImagesRegistry,
  IImagesPresentationFactory,
  IImagePresentationFactoryBag,
  IObjectViewModel,
  IMeasurementViewModel,
  IViewPortController,
  IRaycasterProvider,
  ICenterLineViewModel
} from '@/engine/app/interfaces.ts';
import type {
  IPresentationChangeHandler,
  IUiThreeDPresentationContext,
  IUiTwoDPresentationContext
} from '@/engine/UIPresentation/Interfaces.ts';
import type { IImagesPortContext, SliceType } from '@/engine/app/modules/images/interfaces.ts';
import type { PlanningAlgorithm } from '../PlanningAlgorithm/PlanningAlgorithm';
import type { ViewsService } from './Views.service';
import type { ViewController } from './View.controller';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service';
import type { ICommandProcessor } from './modules/core/interfaces/Command';
import type { IRemoveCommandsFactory } from './modules/indications/IndicationRemoveHighlighter';
import type { ImagesService } from './modules/images/Images.service';
import { Drawer } from '../UIPresentation/Drawer';
import { CenterlineImagesPresentation } from '../UIPresentation/CenterlineImagesPresentation';
import { StubPresentation } from '../UIPresentation/StubPresentation';

export class ImagesPresentationFactory implements IImagesPresentationFactory {
  private _registry: IPresentationCreatorImagesRegistry;

  private _bag: IImagePresentationFactoryBag;

  public constructor(
    registry: IPresentationCreatorImagesRegistry,
    planningAlgorithm: PlanningAlgorithm,
    viewsService: ViewsService,
    viewController: ViewController,
    loggerService: ObservabilityLoggerService,
    commandProcessor: ICommandProcessor,
    removeIndicationCommandsFactory: IRemoveCommandsFactory,
    viewportController: IViewPortController,
    imagesService: ImagesService,
    viewPortDescriptor: string,
    mainSliceType: SliceType
  ) {
    this._registry = registry;
    this._bag = {
      planningAlgorithm: planningAlgorithm,
      viewsService: viewsService,
      viewController: viewController,
      drawer: new Drawer(),
      loggerService: loggerService,
      commandProcessor: commandProcessor,
      removeIndicationCommandsFactory: removeIndicationCommandsFactory,
      viewportController: viewportController,
      imagesService: imagesService,
      viewPortDescriptor: viewPortDescriptor,
      mainSliceType: mainSliceType
    };
  }

  public setRaycasterProvider(raycasterProvider?: IRaycasterProvider): void {
    this._bag.raycasterProvider = raycasterProvider;
  }

  public createThreeDObjectPresentation(
    imagePort: IImagesPortContext,
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._registry
      .resolveImageThreeDObjectCreator(viewModel.kind)
      ?.createPresentation(this._bag, imagePort, viewModel, changeHandler);
  }

  public createThreeDMeasurementPresentation(
    imagePort: IImagesPortContext,
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._registry
      .resolveImageThreeDMeasurementCreator(viewModel.kind)
      ?.createPresentation(this._bag, imagePort, viewModel, changeHandler);
  }

  public createTwoDObjectPresentation(
    imagePort: IImagesPortContext,
    viewModel: IObjectViewModel
  ): IUiTwoDPresentationContext | undefined {
    return this._registry
      .resolveImageTwoDObjectCreator(viewModel.kind)
      ?.createPresentation(this._bag, imagePort, viewModel);
  }

  public createTwoDMeasurementPresentation(
    imagePort: IImagesPortContext,
    viewModel: IMeasurementViewModel
  ): IUiTwoDPresentationContext | undefined {
    return this._registry
      .resolveImageTwoDMeasurementCreator(viewModel.kind)
      ?.createPresentation(this._bag, imagePort, viewModel);
  }

  public createCenterLinePresentation(
    imagesPortContext: IImagesPortContext,
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext {
    if (viewModel.alongSliceDescriptor !== this._bag.mainSliceType) {
      return {
        presentation: new CenterlineImagesPresentation(
          imagesPortContext,
          this._bag.drawer,
          this._bag.planningAlgorithm,
          viewModel,
          changeHandler
        )
      };
    }
    return {
      presentation: new StubPresentation()
    };
  }
}
